import type { ReactNode } from "react";
import { classNames } from "../../classNames";
import { useStoredState } from "../../hooks";
import { CommandPalette, type CommandItem } from "../CommandPalette/CommandPalette";
import { ContextMenu } from "../ContextMenu/ContextMenu";
import { Footer, type FooterProps } from "../Footer/Footer";
import { Sidebar, type NavGroup } from "../Sidebar/Sidebar";
import { type NotificationItem, Topbar, type TopbarUser } from "../Topbar/Topbar";
import { useCommandPalette, usePaletteShortcut } from "./useCommandPalette";
import { useMobileDrawer } from "./useMobileDrawer";
import { useShellContextMenu } from "./useShellContextMenu";
import { useViewChange } from "./useViewChange";

const MAIN_ID = "nb-main";
const COLLAPSED_STORAGE_KEY = "nabble.sidebar.collapsed";

export interface AppShellProps {
  nav: NavGroup[];
  active?: string;
  onNavigate?: (id: string) => void;
  onHome?: () => void;
  brandIcon?: string;
  product?: string;
  user?: TopbarUser;
  /** Entries of the ⌘F / ⌘K command palette. */
  commands?: CommandItem[];
  notifications?: NotificationItem[];
  onLogout?: () => void;
  onProfile?: () => void;
  footer?: FooterProps;
  /** Shown in the box at the bottom of the sidebar (e.g. the organization switcher). */
  sidebarFooter?: ReactNode;
  /** Changes on navigation: replays the view entrance and closes the mobile drawer. */
  viewKey?: string;
  children: ReactNode;
}

/** Every authenticated view lives inside AppShell: sidebar, header, content, footer. */
export function AppShell({
  nav,
  active,
  onNavigate,
  onHome,
  brandIcon,
  product,
  user,
  commands = [],
  notifications,
  onLogout,
  onProfile,
  footer,
  sidebarFooter,
  viewKey,
  children,
}: AppShellProps): React.JSX.Element {
  const drawer = useMobileDrawer();
  const [collapsed, setCollapsed] = useStoredState<boolean>(COLLAPSED_STORAGE_KEY, false);
  const palette = useCommandPalette();
  const shellMenu = useShellContextMenu({ collapsed, onSearch: () => palette.openPalette(), onCollapsedChange: setCollapsed });

  usePaletteShortcut(palette.open, () => {
    drawer.close();
    palette.openPalette();
  });
  useViewChange(viewKey, MAIN_ID, drawer.close);

  return (
    <div className={classNames("nb-shell", drawer.open && "is-mobile-open", collapsed && "is-collapsed")} {...shellMenu.areaProps}>
      <a className="nb-skip" href={`#${MAIN_ID}`}>
        Skip to content
      </a>
      <Topbar
        user={user}
        product={product}
        onHome={onHome}
        onMenu={drawer.show}
        onSearch={palette.openPalette}
        onLogout={onLogout}
        onProfile={onProfile}
        notifications={notifications}
        sidebarCollapsed={collapsed}
      />
      <div className="nb-shell-overlay" onClick={drawer.close} aria-hidden="true" />
      <div className="nb-shell-body">
        <Sidebar
          nav={nav}
          active={active}
          onNavigate={(id) => {
            drawer.close();
            onNavigate?.(id);
          }}
          onHome={onHome}
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          onClose={drawer.close}
          brandIcon={brandIcon}
          product={product}
          footer={sidebarFooter}
        />
        <div className="nb-shell-main">
          <main className="nb-shell-content" id={MAIN_ID} tabIndex={-1}>
            <div className="nb-shell-inner">
              <div className="nb-view" key={viewKey}>
                {children}
              </div>
            </div>
          </main>
          <Footer {...footer} />
        </div>
      </div>
      <ContextMenu {...shellMenu.menuProps} ariaLabel="Suite" />
      <CommandPalette open={palette.open} onClose={palette.close} items={commands} initialQuery={palette.seed} />
    </div>
  );
}
